import { Textarea } from "@pepperstone/pepper-ui-kit";
import type React from "react";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { FileDropzone } from "@/components/delete-saved-payment-dialog/file-dropzone/FileDropzone";
import type { DeleteFormData } from "@/components/delete-saved-payment-dialog/types";
import { ErrorMessage } from "@/components/ErrorMessage";

type DeleteFormProps = {
  formData: DeleteFormData;
  setFormData: React.Dispatch<React.SetStateAction<DeleteFormData>>;
};

const ALLOWED_FILE_TYPES = ["application/pdf", "image/png", "image/jpeg"];

export const DeleteForm = forwardRef<HTMLFormElement, DeleteFormProps>(
  ({ formData, setFormData, ...props }, ref) => {
    const { t } = useTranslation();

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileInput = e.target;
      let state = "touched";
      const file = e.target.files?.[0];
      const size = file?.size ? file?.size / (1024 * 1024) : 0;
      const type = file?.type;

      if (type && !ALLOWED_FILE_TYPES.includes(type)) {
        fileInput.setCustomValidity(
          t("deposit.methods.saved_methods.file_type_not_allowed")
        );
        state = "invalid";
      } else if (size > 1) {
        fileInput.setCustomValidity(
          t("deposit.methods.saved_methods.file_size_exceeds")
        );
        state = "invalid";
      } else {
        fileInput.setCustomValidity("");
      }

      if (file) {
        setFormData({
          ...formData,
          file: {
            value: file,
            state,
            ...{ message: fileInput.validationMessage },
          },
        });
      } else {
        setFormData({ ...formData, file: undefined });
      }
    };

    const handleReasonBlur = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      const state = !e.target.validity.valid ? "invalid" : "touched";
      setFormData({
        ...formData,
        reason: { value: value || "", state },
      });
    };

    const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      const state = e.target.validity.valid ? "valid" : "invalid";
      setFormData({
        ...formData,
        reason: { value: value || "", state },
      });
    };

    return (
      <form
        ref={ref}
        {...props}
        className="ps-flex ps-gap-4 ps-flex-col ps-py-4"
      >
        <p className="ps-text-body-md">
          {t("deposit.methods.saved_methods.remove_warning")}
        </p>
        <Textarea
          id="DeleteSavedPaymentAccountReason"
          required
          minLength={3}
          onBlur={handleReasonBlur}
          onChange={handleReasonChange}
          errorText={
            formData.reason?.state === "invalid" ||
            (formData.reason?.state === "touched" &&
              formData.reason?.value === "")
              ? t("deposit.methods.saved_methods.reason_required")
              : ""
          }
          name="reason"
          label={t("deposit.methods.saved_methods.reason_label")}
          placeholder={t("deposit.methods.saved_methods.reason_placeholder")}
          variant="background"
          rows={2}
          max={250}
          value={formData.reason?.value}
        />
        <div className="ps-flex ps-flex-col ps-gap-2">
          <label
            className="ps-tw-reset ps-font-sans ps-text-label-v2-md ps-text-v2-neutral-1200 ps-text-color-inherit"
            htmlFor="file"
          >
            {t("deposit.methods.saved_methods.upload_proof_label")}{" "}
            <span className="ps-text-v2-text-base-subdued">
              {t("deposit.methods.saved_methods.optional")}
            </span>
          </label>
          <FileDropzone
            onChange={handleFileChange}
            uploadProps={{ accept: "image/png,image/jpeg,application/pdf" }}
          >
            <FileDropzone.FileDropzoneIcon icon="arrow-up-narrow" size={24} />
            <FileDropzone.FileDropzoneText />
          </FileDropzone>
          <p>
            <span
              className={`${formData.file?.state === "invalid" ? "ps-text-v2-red-500" : "ps-text-v2-text-base-subdued"} ps-text-body-v2-sm ps-text-ellipsis ps-inline-block ps-w-full ps-whitespace-nowrap ps-overflow-hidden`}
            >
              {(formData.file?.value?.size
                ? formData.file?.value?.size / (1024 * 1024)
                : 0
              ).toFixed(2)}
              {t("deposit.methods.saved_methods.file_size_display")} (
              {formData.file?.value?.name})
            </span>
          </p>
          {formData.file?.state === "invalid" && formData.file?.message && (
            <ErrorMessage message={formData.file?.message} />
          )}
        </div>
      </form>
    );
  }
);
